/**
 * LetterboxUi — the letter socket on the C5 console, and the letter it opens.
 *
 * Doctrine (docs/design/ui-c5-indigo-ink.md): "Letters / mail — wax-sealed
 * envelope → opens to slip." One of the bottom band's sockets is the box: a
 * sealed envelope with a slow ember when something waits, a faint ring when
 * the box is empty. Clicking opens the oldest unread letter as a sheet of
 * paper; with nothing unread it shows the stack of letters already kept.
 *
 * Everything decidable lives in `letterbox.ts` (pure, unit-tested). This file
 * owns DOM, fetch and timers only, so it needs no test harness.
 *
 * Letters carry no mechanics: no objectives, no rewards, no travel. A
 * `smallGift` is a sentence about a pressed flower, and nothing else.
 */

import type * as Phaser from 'phaser';
import letterboxCss from './c5-letterbox.css?inline';
import { getString } from '../locale';
import {
  emitLetterArrived,
  emitLetterOpened,
  onDayChanged,
  onLetterboxToggle,
  type HudUnsub,
} from '../../game/HudState';
import { readDayCount } from './dayCount';
import {
  archivedLetters,
  bodyParagraphs,
  deliverDueLetter,
  deliveredDay,
  emptyLetterboxState,
  hasUnread,
  loadLetterboxState,
  markRead,
  saveLetterboxState,
  unreadLetters,
  type LetterboxState,
} from './letterbox';
import { LETTERS_URL, parseLettersFile, type Letter } from './letters.schema';

const STYLE_ELEMENT_ID = 'c5-letterbox-styles';

/** Beat between the slip announcement and the socket lighting up. Gentle. */
const ARRIVAL_SLIP_DELAY_MS = 900;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

export interface LetterboxUiDeps {
  /** Show a loose paper slip on the painting (C5Frame supplies its own). */
  showSlip: (text: string) => void;
}

export class LetterboxUi {
  private readonly socket: HTMLElement;
  private readonly envelope: HTMLDivElement;
  private readonly veil: HTMLDivElement;
  private readonly deps: LetterboxUiDeps;

  private letters: Letter[] = [];
  private state: LetterboxState = emptyLetterboxState();
  private game: Phaser.Game | null = null;
  private readonly unsubs: HudUnsub[] = [];
  private readonly timers: number[] = [];
  private onKeyDown: ((e: KeyboardEvent) => void) | null = null;
  private keyboardWasEnabled = true;
  private open = false;
  private destroyed = false;

  /**
   * @param socket  the band socket that becomes the letterbox
   * @param overlayParent  element the opened letter is rendered into (#c5-frame)
   */
  constructor(socket: HTMLElement, overlayParent: HTMLElement, deps: LetterboxUiDeps) {
    injectStyles();
    this.deps = deps;

    this.socket = socket;
    this.socket.classList.add('c5-letter-socket');
    this.socket.setAttribute('role', 'button');
    this.socket.tabIndex = 0;
    this.envelope = el('div', 'c5-letter-envelope');
    this.envelope.hidden = true;
    this.socket.appendChild(this.envelope);
    this.socket.addEventListener('click', () => this.toggle());
    this.socket.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggle();
      }
    });

    this.veil = el('div', 'c5-letter-veil');
    this.veil.hidden = true;
    this.veil.addEventListener('click', (e) => {
      if (e.target === this.veil) this.close();
    });
    overlayParent.appendChild(this.veil);

    this.renderSocket();
  }

  /** Wire to the HUD bus and load content. Safe to call once, after boot. */
  attach(game: Phaser.Game): void {
    this.game = game;
    this.unsubs.push(
      onDayChanged(game, (d) => this.runDelivery(d.day)),
      onLetterboxToggle(game, () => this.toggle()),
    );

    this.onKeyDown = (e: KeyboardEvent) => {
      if (!this.open) return;
      if (e.key === 'Escape') {
        // Swallow it: Escape must not also reach the pause overlay.
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    };
    window.addEventListener('keydown', this.onKeyDown, true);

    void this.load();
  }

  /** Fetch + validate the content file, then run the boot catch-up delivery. */
  private async load(): Promise<void> {
    this.state = loadLetterboxState(safeStorage());
    try {
      const resp = await fetch(LETTERS_URL);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      this.letters = parseLettersFile(await resp.json());
    } catch (err) {
      console.warn('[letterbox] could not load letters', err);
      this.letters = [];
    }
    if (this.destroyed) return;
    this.renderSocket();
    this.runDelivery(readDayCount());
  }

  /** At most one letter per day; see letterbox.ts for the rules. */
  private runDelivery(day: number): void {
    if (this.letters.length === 0) return;
    const result = deliverDueLetter(this.letters, { day }, this.state);
    if (!result.delivered) return;
    this.state = result.state;
    saveLetterboxState(this.state, safeStorage());

    const arrived = result.delivered;
    this.timers.push(
      window.setTimeout(() => {
        if (this.destroyed) return;
        this.deps.showSlip(getString('c5.letterbox.arrived'));
        this.renderSocket();
      }, ARRIVAL_SLIP_DELAY_MS),
    );
    if (this.game) emitLetterArrived(this.game, { id: arrived.id, from: arrived.from });
  }

  // ── socket ───────────────────────────────────────────────────────────────

  private renderSocket(): void {
    const unread = hasUnread(this.letters, this.state);
    const kept = archivedLetters(this.letters, this.state).length > 0;
    this.socket.classList.toggle('c5-letters-unread', unread);
    this.socket.classList.toggle('c5-letters-archive', !unread && kept);
    this.socket.classList.toggle('c5-empty', !unread && !kept);
    this.envelope.hidden = !unread;
    this.socket.setAttribute(
      'aria-label',
      getString(
        unread
          ? 'c5.letterbox.socket.unread'
          : kept
            ? 'c5.letterbox.socket.archive'
            : 'c5.letterbox.socket.empty',
      ),
    );
  }

  // ── open / close ─────────────────────────────────────────────────────────

  toggle(): void {
    if (this.open) {
      this.close();
      return;
    }
    const [next] = unreadLetters(this.letters, this.state);
    if (next) this.openLetter(next);
    else this.openArchive();
  }

  private openLetter(letter: Letter): void {
    const first = !this.state.read.includes(letter.id);
    this.state = markRead(this.state, letter.id);
    saveLetterboxState(this.state, safeStorage());
    this.renderSocket();

    this.veil.replaceChildren(this.buildSheet(letter));
    this.show();
    if (first && this.game) {
      emitLetterOpened(this.game, { id: letter.id, from: letter.from });
    }
  }

  private openArchive(): void {
    const kept = archivedLetters(this.letters, this.state);
    const stack = el('div', 'c5-letter-stack');
    stack.setAttribute('aria-label', getString('c5.letterbox.a11y.archive'));

    const title = el('div', 'c5-letter-stack-title');
    title.textContent = getString('c5.letterbox.archive_title');
    stack.appendChild(title);

    if (kept.length === 0) {
      const empty = el('div', 'c5-letter-stack-empty');
      empty.textContent = getString('c5.letterbox.archive_empty');
      stack.appendChild(empty);
    }
    for (const letter of kept) {
      const item = el('button', 'c5-letter-stack-item');
      item.type = 'button';
      item.textContent = `${letter.from} `;
      const day = el('span');
      day.textContent = dayStamp(deliveredDay(this.state, letter.id));
      item.appendChild(day);
      item.addEventListener('click', () => this.openLetter(letter));
      stack.appendChild(item);
    }

    this.veil.replaceChildren(stack);
    this.show();
  }

  private buildSheet(letter: Letter): HTMLElement {
    const sheet = el('div', 'c5-letter-sheet');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-label', getString('c5.letterbox.a11y.letter'));

    // The seal that just broke — it rotates and settles once, then rests.
    sheet.appendChild(el('div', 'c5-letter-seal'));

    const from = el('div', 'c5-letter-from');
    from.textContent = letter.from;
    sheet.appendChild(from);

    if (letter.subject) {
      const subject = el('div', 'c5-letter-subject');
      subject.textContent = letter.subject;
      sheet.appendChild(subject);
    }
    sheet.appendChild(el('div', 'c5-letter-rule'));

    const body = el('div', 'c5-letter-body');
    for (const para of bodyParagraphs(letter)) {
      const p = el('p');
      p.textContent = para;
      body.appendChild(p);
    }
    sheet.appendChild(body);

    if (letter.smallGift) {
      const gift = el('div', 'c5-letter-gift');
      gift.textContent = getString('c5.letterbox.gift').replace(
        '{0}',
        letter.smallGift.name,
      );
      if (letter.smallGift.note) {
        const note = el('em');
        note.textContent = letter.smallGift.note;
        gift.appendChild(note);
      }
      sheet.appendChild(gift);
    }

    const sign = el('div', 'c5-letter-sign');
    sign.textContent = letter.signature ?? `— ${letter.from}`;
    sheet.appendChild(sign);

    const day = deliveredDay(this.state, letter.id);
    if (day > 0) {
      const stamp = el('div', 'c5-letter-stamp');
      stamp.textContent = dayStamp(day);
      sheet.appendChild(stamp);
    }

    const close = el('button', 'c5-letter-close');
    close.type = 'button';
    close.textContent = getString('c5.letterbox.close');
    close.addEventListener('click', () => this.close());
    sheet.appendChild(close);

    return sheet;
  }

  private show(): void {
    this.veil.hidden = false;
    this.open = true;
    // Suspend game keys so reading a letter never walks the traveler.
    const keyboard = this.game?.input?.keyboard;
    if (keyboard) {
      this.keyboardWasEnabled = keyboard.enabled;
      keyboard.enabled = false;
    }
    requestAnimationFrame(() => this.veil.classList.add('c5-open'));
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.veil.classList.remove('c5-open');
    const keyboard = this.game?.input?.keyboard;
    if (keyboard) keyboard.enabled = this.keyboardWasEnabled;
    this.timers.push(
      window.setTimeout(() => {
        if (this.open) return;
        this.veil.hidden = true;
        this.veil.replaceChildren();
      }, 260),
    );
  }

  destroy(): void {
    this.destroyed = true;
    for (const off of this.unsubs) off();
    this.unsubs.length = 0;
    for (const t of this.timers) window.clearTimeout(t);
    this.timers.length = 0;
    if (this.onKeyDown) {
      window.removeEventListener('keydown', this.onKeyDown, true);
      this.onKeyDown = null;
    }
    this.envelope.remove();
    this.veil.remove();
    this.socket.classList.remove(
      'c5-letter-socket',
      'c5-letters-unread',
      'c5-letters-archive',
    );
    this.game = null;
  }
}

// ── module helpers ─────────────────────────────────────────────────────────

function injectStyles(): void {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = letterboxCss;
  document.head.appendChild(style);
}

function dayStamp(day: number): string {
  return getString('c5.letterbox.day_stamp').replace('{0}', String(Math.max(1, day)));
}

function safeStorage(): Storage | null {
  try {
    return (globalThis as unknown as { localStorage?: Storage }).localStorage ?? null;
  } catch {
    return null;
  }
}
